import { useState, useEffect } from 'react';
import warehousesService from '../../../services/warehousesService';
import api from '../../../services/api';

const INITIAL_WAREHOUSE_FORM = {
  nazwa: '', kod: '', location_id: '', adres: '', telefon: '',
  email: '', nip: '', miasto: '', kod_pocztowy: '',
  wojewodztwo: '', typ_magazynu: 'sklep', opis: '', kierownik_id: ''
};

const INITIAL_EMPLOYEE_FORM = {
  warehouse_id: '', user_login: '', rola: 'pracownik', uprawnienia: ''
};

export function useWarehouseManagementData() {
  const [warehouses, setWarehouses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('warehouses');
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [warehouseForm, setWarehouseForm] = useState(INITIAL_WAREHOUSE_FORM);
  const [employeeForm, setEmployeeForm] = useState(INITIAL_EMPLOYEE_FORM);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [warehousesRes, usersRes, locationsRes] = await Promise.all([
        warehousesService.getWarehousesWithStatus(),
        api.get('/admin/users'),
        api.get('/locations/')
      ]);

      if (warehousesRes.success) {
        const warehouseList = warehousesRes.data || [];
        setWarehouses(warehouseList);
        const allEmployees = [];
        warehouseList.forEach(warehouse => {
          if (warehouse.assigned_users) {
            warehouse.assigned_users.forEach(assignment => {
              allEmployees.push({
                id: `${warehouse.id}-${assignment.user_login}`,
                login: assignment.user_login,
                rola: assignment.rola,
                warehouseId: warehouse.id,
                warehouseName: warehouse.nazwa,
                data_od: assignment.data_od,
                data_do: assignment.data_do,
                aktywny: assignment.aktywny
              });
            });
          }
        });
        setEmployees(allEmployees);
      }

      if (usersRes.data?.success) {
        setUsers(usersRes.data?.data?.users || []);
      }

      if (locationsRes.data?.success) {
        setLocations(locationsRes.data?.data || []);
      }
    } catch {
      setError('Blad podczas ladowania danych');
    } finally {
      setLoading(false);
    }
  };

  const handleWarehouseSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const warehouseData = { ...warehouseForm, kod_magazynu: warehouseForm.kod };
      delete warehouseData.kod;

      const result = editingWarehouse
        ? await warehousesService.updateWarehouseWithStatus(editingWarehouse.id, warehouseData)
        : await warehousesService.createWarehouse(warehouseData);

      if (result.success) {
        setSuccess(result.message);
        setShowWarehouseModal(false);
        resetWarehouseForm();
        loadData();
      } else {
        setError(result.error);
      }
    } catch {
      setError('Blad podczas zapisywania magazynu');
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await warehousesService.assignEmployee(
        employeeForm.warehouse_id,
        employeeForm.user_login,
        employeeForm.rola,
        employeeForm.uprawnienia
      );
      if (result.success) {
        setSuccess(result.message);
        setShowEmployeeModal(false);
        resetEmployeeForm();
        loadData();
      } else {
        setError(result.error);
      }
    } catch {
      setError('Blad podczas przypisywania pracownika');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWarehouse = async (warehouseId) => {
    if (window.confirm('Czy na pewno chcesz usunac ten magazyn?')) {
      try {
        const result = await warehousesService.deleteWarehouseWithStatus(warehouseId);
        if (result.success) {
          setSuccess(result.message);
          loadData();
        } else {
          setError(result.error);
        }
      } catch {
        setError('Blad podczas usuwania magazynu');
      }
    }
  };

  const handleRemoveEmployee = async (warehouseId, userLogin) => {
    if (window.confirm('Czy na pewno chcesz usunac to przypisanie?')) {
      try {
        const result = await warehousesService.removeEmployeeAssignment(warehouseId, userLogin);
        if (result.success) {
          setSuccess(result.message);
          loadData();
        } else {
          setError(result.error);
        }
      } catch {
        setError('Blad podczas usuwania przypisania');
      }
    }
  };

  const editWarehouse = (warehouse) => {
    setEditingWarehouse(warehouse);
    setWarehouseForm({
      nazwa: warehouse.nazwa || '',
      kod: warehouse.kod_magazynu || warehouse.kod || '',
      location_id: warehouse.location_id || '',
      adres: warehouse.adres || '',
      telefon: warehouse.telefon || '',
      email: warehouse.email || '',
      nip: warehouse.nip || '',
      miasto: warehouse.miasto || '',
      kod_pocztowy: warehouse.kod_pocztowy || '',
      wojewodztwo: warehouse.wojewodztwo || '',
      typ_magazynu: warehouse.typ_magazynu || 'sklep',
      opis: warehouse.opis || '',
      kierownik_id: warehouse.kierownik_id || ''
    });
    setShowWarehouseModal(true);
  };

  const resetWarehouseForm = () => {
    setWarehouseForm(INITIAL_WAREHOUSE_FORM);
    setEditingWarehouse(null);
  };

  const resetEmployeeForm = () => setEmployeeForm(INITIAL_EMPLOYEE_FORM);

  const openNewWarehouseModal = () => {
    resetWarehouseForm();
    setShowWarehouseModal(true);
  };

  const openNewEmployeeModal = () => {
    resetEmployeeForm();
    setShowEmployeeModal(true);
  };

  const closeWarehouseModal = () => {
    setShowWarehouseModal(false);
    resetWarehouseForm();
  };

  const closeEmployeeModal = () => {
    setShowEmployeeModal(false);
    resetEmployeeForm();
  };

  return {
    warehouses, employees, users, locations,
    loading, error, success, setError, setSuccess,
    activeTab, setActiveTab,
    showWarehouseModal, showEmployeeModal,
    editingWarehouse,
    warehouseForm, setWarehouseForm,
    employeeForm, setEmployeeForm,
    handleWarehouseSubmit, handleEmployeeSubmit,
    handleDeleteWarehouse, handleRemoveEmployee,
    editWarehouse,
    openNewWarehouseModal, openNewEmployeeModal,
    closeWarehouseModal, closeEmployeeModal
  };
}
